import { useCallback, useEffect, useState } from "react";
import { lastMinuteService } from "../../services/lastMinuteService";

// Received opportunity requests (recipient inbox, design 9b/9e) + Accept/Ignore.
// Durable: less-active agents find them later. Ignore is SILENT — the row simply
// leaves the list, and no signal ever reaches the sender (Invariant 9, enforced by
// the BE which never surfaces 'ignored' to the sender).
export const useReceivedRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async (signal) => {
    setLoading(true);
    const res = await lastMinuteService.getReceivedRequests(signal);
    if (res.success) {
      setRequests(res.data);
      setError(null);
    } else {
      setError(res.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [fetch]);

  const accept = async (id) => {
    const res = await lastMinuteService.acceptRequest(id);
    if (res.success) {
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
    }
    return res;
  };

  const ignore = async (id) => {
    const res = await lastMinuteService.ignoreRequest(id);
    if (res.success) {
      // Silent: the row disappears from the recipient inbox; nothing goes to sender.
      setRequests((prev) => prev.filter((r) => r.id !== id));
    }
    return res;
  };

  return { requests, loading, error, refetch: fetch, accept, ignore };
};

// Sender-side map: listing_id -> { status: 'pending'|'accepted', channel_cid }. The
// BE collapses 'ignored' to 'pending', so only these two states ever exist here
// (Invariant 9). Powers the feed card's "Request pending" / "Open chat" swap.
export const useSentRequestsMap = (enabled = true) => {
  const [byListingId, setByListingId] = useState(new Map());

  const load = useCallback(async (signal) => {
    if (!enabled) return;
    const res = await lastMinuteService.getSentRequests(signal);
    if (!res.success) return;
    const m = new Map();
    res.data.forEach((r) => {
      if (r.listing_id != null) m.set(r.listing_id, { status: r.status, channel_cid: r.channel_cid });
    });
    setByListingId(m);
  }, [enabled]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [load]);

  return { byListingId, reload: load };
};

export default useReceivedRequests;
